import { AuthProvider } from 'react-admin';
import { UserType } from '@/types';
import { canAccess } from '../commons/canAccess';
import { clearReceiptBlobCache } from '@/modules/receipts/receiptApi';
import { clearQuoteDriveThumbnailCache } from '@/utils/quote_attachment_utils';

/** Clear all in-memory module caches (receipt blobs, quote drive thumbnails). */
function clearSessionCaches(): void {
    clearReceiptBlobCache();
    clearQuoteDriveThumbnailCache();
}

export const authProvider: AuthProvider = {
    login: async ({ email, password, recaptchaToken }) => {
        // Remove any stale user credentials and clear module caches before exchanging new credentials.
        // Defense in depth: if login fails, stale storage can't persist. Matches the queryClient.clear()
        // in LoginForm to cover all paths into an authenticated session.
        localStorage.removeItem(import.meta.env.VITE_USER_STORAGE_KEY);
        clearSessionCaches();

        const form = new FormData();
        form.append('username', email);
        form.append('password', password);
        if (recaptchaToken) {
            form.append('recaptcha_token', recaptchaToken);
        }

        const request = new Request(import.meta.env.VITE_APP_API_BACKEND_ROOT + '/auth/token', {
            method: 'POST',
            body: form,
        });
        let response;
        try {
            response = await fetch(request);
        } catch (_error) {
            throw new Error('Network error');
        }

        const auth = await response.json();

        if (response.status < 200 || response.status >= 300) {
            if (auth && auth.detail) {
                throw new Error(auth.detail);
            } else{
                throw new Error(response.statusText);
            }
        }

        localStorage.setItem(import.meta.env.VITE_USER_STORAGE_KEY, JSON.stringify(auth));

        return { redirectTo: '/' };
    },
    logout: () => {
        localStorage.removeItem(import.meta.env.VITE_USER_STORAGE_KEY);
        clearSessionCaches();
        return Promise.resolve();
    },
    checkError: () => Promise.resolve(),
    checkAuth: () =>
        localStorage.getItem(import.meta.env.VITE_USER_STORAGE_KEY)
            ? Promise.resolve()
            : Promise.reject(),
    canAccess: async ({ signal, ...params }) => {
        return canAccess(params);
    },
    getIdentity: () => {
        const item = localStorage.getItem(import.meta.env.VITE_USER_STORAGE_KEY);
        if (!item)
            return Promise.reject();

        const authCredentials = JSON.parse(item) as UserType;
        return Promise.resolve({ 
            id: authCredentials.id,
            fullName: authCredentials.name,
            administrator: authCredentials.administrator,
            permissions: authCredentials.permissions,
        });
    },
};
